import React, { useEffect, useState, useCallback } from 'react';
import { Card, Row, Col } from 'react-bootstrap';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ReferenceLine
} from 'recharts';
import { getReputationHistory } from '../services/api';
import ErrorFallback from './ErrorFallback';

const CustomTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
        return (
            <div className="bg-white rounded shadow-sm p-2 text-sm fw-semibold" style={{ fontSize: '13px' }}>
                {payload[0].name || 'Growth'}: <strong>{payload[0].value.toLocaleString()}</strong>
            </div>
        );
    }
    return null;
};

const GrowthInterest = ({ influencerId, platformAccountType }) => {
    const [followersData, setFollowersData] = useState([]);
    const [likesData, setLikesData] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getReputationHistory(influencerId, platformAccountType);
                if (response.status === 404) {
                    setError("Profile not found. Please check the influencer ID.");
                } else if (response.data?.length > 0) {
                    const formatted = response.data.map((item) => ({
                        month: new Date(item.month + '-01').toLocaleDateString('en-US', { month: 'short' }),
                        followers: item.followerCount,
                        likes: item.averageLikes,
                    }));
                    setFollowersData(formatted);
                    setLikesData(formatted);
                    setError(null);
                }
            } catch (err) {
                console.error(err);
                setError("An error occurred while fetching the profile details.");
            }
        };
        fetchData();
    }, [influencerId, platformAccountType]);

    const formatFollowers = useCallback(count => {
        if (count >= 1_000_000_000) return (count / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
        if (count >= 1_000_000) return (count / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
        if (count >= 1_000) return (count / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
        return count;
    }, []);

    const renderChart = (dataKey, data, label) => {
        const maxIndex = data.reduce(
            (maxIdx, point, idx, arr) => (point[dataKey] > arr[maxIdx][dataKey] ? idx : maxIdx),
            0
        );

        const renderCustomDot = ({ cx, cy, index }) => {
            return index === maxIndex ? (
                <circle cx={cx} cy={cy} r={2} stroke="#680DE4" strokeWidth={8} fill="#000" />
            ) : null;
        };

        // Render vertical reference lines excluding first and last point
        const customVerticalLines = data.slice(1, -1).map((point) => (
            <ReferenceLine
                key={`ref-${point.month}`}
                x={point.month}
                stroke="#535862"
                strokeWidth={0.1}
                strokeDasharray=""
            />
        ));

        return (
            <ResponsiveContainer width="106%" height={300}>
                <AreaChart data={data}>
                    <defs>
                        <linearGradient id={`gradient-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#680DE4" stopOpacity={0.4} />
                            <stop offset="100%" stopColor="#680DE4" stopOpacity={0.05} />
                        </linearGradient>
                    </defs>

                    {/* Dashed horizontal grid only */}
                    <CartesianGrid horizontal strokeDasharray="3 3" stroke="#E0E0E0" vertical={false} />

                    {/* Custom vertical lines via ReferenceLine */}
                    {customVerticalLines}

                    <XAxis
                        dataKey="month"
                        tick={{ fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                    />
                    <YAxis
                        domain={[
                            (dataMin) => Math.floor(dataMin / 100) * 100,
                            (dataMax) => Math.ceil(dataMax / 100) * 100,
                        ]}
                        tick={{ fontSize: 12 }}
                        interval={0}
                        tickCount={6}
                        tickFormatter={formatFollowers}
                        axisLine={false}
                        tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                        type="monotone"
                        dataKey={dataKey}
                        name={label}
                        stroke="#680DE4"
                        strokeWidth={3}
                        fill={`url(#gradient-${dataKey})`}
                        dot={renderCustomDot}
                    />
                </AreaChart>
            </ResponsiveContainer>
        );
    };
    // 👉 Return fallback if empty or error
    if (error || followersData.length === 0 || likesData.length === 0) {
        return (
          <ErrorFallback
            title="Growth & Interests"
            image={require('../assets/icons/growth-empty.svg').default} // update to match your image path if needed
          />
        );
      }

    return (
        <Card id="growth-interest" className="growth-and-interest border rounded-0">
            <Card.Body>
                <div className="info-header-title mainHeading mb-4">Growth & Interests</div>
                <Row className="graphs-container p-2 gap-2">
                    <Col md={6} sm={12} className="graph-body">
                        <p className="graph-header mb-0 p-0">Followers Growth</p>
                        <small className="graph-sub-header">Trend of total followers</small>
                        <div className="mt-3 p-0">
                            {renderChart("followers", followersData, "Followers Growth")}
                        </div>
                    </Col>
                    <Col md={6} sm={12} className="graph-body">
                        <p className="graph-header m-0">Likes Growth</p>
                        <small className="graph-sub-header">Trend of average likes per post</small>
                        <div className="mt-3">
                            {renderChart("likes", likesData, "Likes Growth")}
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

export default GrowthInterest;
